import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import Helmet from "react-helmet";
import styled from "styled-components";
import { Flex, Box } from "reflexbox";
import CI from "@trackcode/ci";
import { StandardButton, Icon } from "@trackcode/ui";

import { ActionBar } from "features/app/components/ActionBar";
import { loadDashboardData } from "./actions";
import { selectDashboardData, selectDashboardLoading } from "./selectors";
import MetricsCard from "./components/MetricsCard";
import DateRangeFilter from "./components/DateRangeFilter";
import AdminRoute from "./components/AdminRoute";
import { resolveWarehouseScansRow } from "./warehouseScans";
import Loading from "../../components/Loading/Loading";

const Container = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f8f9fa;
`;

const Content = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
`;

const Header = styled.div`
  background: white;
  padding: 20px;
  border-bottom: 1px solid ${CI.color.gray};
  margin-bottom: 20px;
`;

const Title = styled.h1`
  margin: 0;
  color: ${CI.color.darkGray};
  font-size: 24px;
  font-weight: 600;
`;

const Subtitle = styled.p`
  margin: 8px 0 0 0;
  color: ${CI.color.mediumGray};
  font-size: 14px;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

/** One-Time-App + Lager-Scans übereinander in einer Grid-Zelle */
const OneTimeAppColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
`;

const RefreshButton = styled(StandardButton)`
  margin-left: auto;
`;

class Dashboard extends Component {
  static propTypes = {
    loadDashboardData: PropTypes.func.isRequired,
    dashboardData: PropTypes.object,
    loading: PropTypes.bool,
    error: PropTypes.string
  };

  constructor(props) {
    super(props);
    
    // Standard: 1. des aktuellen Monats bis heute
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(1);
    startDate.setHours(0, 0, 0, 0);
    
    this.state = {
      startDate,
      endDate,
      rangeKey: "custom"
    };
  }

  componentDidMount() {
    this.loadData();
  }

  loadData = () => {
    this.props.loadDashboardData(
      this.state.startDate,
      this.state.endDate,
      this.state.rangeKey
    );
  };

  handleRefresh = () => {
    this.loadData();
  };

  handleDateChange = (startDate, endDate, rangeKey = "custom") => {
    this.setState({ startDate, endDate, rangeKey }, () => {
      this.loadData();
    });
  };

  getComparisonLabel = () => {
    const labelMap = {
      today: "gestern",
      yesterday: "vorgestern",
      thisWeek: "letzte Woche",
      lastWeek: "vorletzte Woche",
      thisMonth: "letzter Monat",
      lastMonth: "vorletzter Monat",
      thisYear: "letztes Jahr",
      custom: "letzter Monat"
    };

    return labelMap[this.state.rangeKey] || "Letzter Monat";
  };

  render() {
    const { dashboardData, loading, error } = this.props;
    const comparisonLabel = this.getComparisonLabel();
    const warehouseScansRow =
      dashboardData?.warehouseScansRow ??
      resolveWarehouseScansRow(dashboardData?.rawApiData);
    const comparisonWarehouseScansRow =
      dashboardData?.comparison?.warehouseScansRow ??
      resolveWarehouseScansRow(dashboardData?.rawComparisonApiData);

  return (
    <AdminRoute>
      <Container>
        <Helmet title="Admin Dashboard - TrackCode" />
        
        {/* Custom header for dashboard */}
        <div style={{
          background: 'white',
          padding: '16px 20px',
          borderBottom: '1px solid #ddd',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h1 style={{ margin: 0, fontSize: '20px', color: '#333' }}>
            TrackCode Admin Dashboard
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <DateRangeFilter
              startDate={this.state.startDate}
              endDate={this.state.endDate}
              onDateChange={this.handleDateChange}
            />
            <RefreshButton onClick={this.handleRefresh} disabled={loading}>
              <Icon name="refresh" />
              Aktualisieren
            </RefreshButton>
          </div>
        </div>

        <Content>
            <div style={{ 
              background: 'white', 
              padding: '20px', 
              borderRadius: '8px', 
              marginBottom: '20px',
              border: '1px solid #ddd'
            }}>
              <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '14px' }}>
                Übersicht über wichtige Kennzahlen für die Rechnungsstellung
              </p>
              <p style={{ margin: 0, color: '#333', fontSize: '16px', fontWeight: '500' }}>
                Zeitraum: {this.state.startDate.toLocaleDateString('de-DE')} - {this.state.endDate.toLocaleDateString('de-DE')}
              </p>
            </div>

            {loading ? (
              <Loading />
            ) : error ? (
              <div style={{ 
                background: '#f8d7da', 
                color: '#721c24',
                padding: '16px', 
                borderRadius: '4px', 
                marginBottom: '20px',
                border: '1px solid #f5c6cb'
              }}>
                <strong>Fehler beim Laden der Dashboard-Daten:</strong>
                <br />
                {error}
                <br />
                <button 
                  onClick={this.handleRefresh}
                  style={{
                    marginTop: '10px',
                    padding: '8px 16px',
                    background: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Erneut versuchen
                </button>
              </div>
            ) : (
              <>
                {dashboardData?.source && (
                  <div style={{ 
                    background: dashboardData.source === 'api' ? '#d4edda' : '#fff3cd', 
                    padding: '8px 12px', 
                    borderRadius: '4px', 
                    marginBottom: '20px',
                    fontSize: '14px',
                    color: '#155724'
                  }}>
                    Datenquelle: {dashboardData.source === 'api' ? 'Live API' : 'Mock-Daten (API nicht verfügbar)'}
                    {dashboardData.timestamp && (
                      <span style={{ float: 'right', opacity: 0.7 }}>
                        Aktualisiert: {new Date(dashboardData.timestamp).toLocaleTimeString()}
                      </span>
                    )}
                  </div>
                )}
                
                <MetricsGrid>
                  <MetricsCard
                    title="Nutzer pro Logistikunternehmen"
                    data={dashboardData?.usersPerCompany || []}
                    comparisonData={dashboardData?.comparison?.usersPerCompany || []}
                    comparisonLabel={comparisonLabel}
                    type="users"
                  />
                  
                  <MetricsCard
                    title="Status-Updates pro Zeitraum"
                    data={dashboardData?.statusUpdates || []}
                    comparisonData={dashboardData?.comparison?.statusUpdates || []}
                    comparisonLabel={comparisonLabel}
                    type="status"
                  />
                  
                  <OneTimeAppColumn>
                    <MetricsCard
                      title="One-Time-App Nutzung pro Zeitraum"
                      data={dashboardData?.oneTimeAppPerCompany || []}
                      comparisonData={dashboardData?.comparison?.oneTimeAppPerCompany || []}
                      comparisonLabel={comparisonLabel}
                      type="oneTimeApp"
                    />
                    {warehouseScansRow && (
                      <MetricsCard
                        title="Lager-Scans"
                        data={[warehouseScansRow]}
                        comparisonData={
                          comparisonWarehouseScansRow
                            ? [comparisonWarehouseScansRow]
                            : []
                        }
                        comparisonLabel={comparisonLabel}
                        type="warehouseScans"
                      />
                    )}
                  </OneTimeAppColumn>
                </MetricsGrid>
              </>
            )}
          </Content>
        </Container>
      </AdminRoute>
    );
  }
}

const mapStateToProps = state => ({
  dashboardData: selectDashboardData(state),
  loading: selectDashboardLoading(state),
  error: state.dashboard?.error
});

const mapDispatchToProps = {
  loadDashboardData
};

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
